import { useEffect, useState, useRef, useCallback } from "react";
import { IUserData } from "@/types"; // Make sure IUserData is imported if used in props

// Utility: Throttle function
const throttle = (func: Function, limit: number) => {
    let inThrottle: boolean;
    let lastResult: any;
    return function (...args: any[]) {
        if (!inThrottle) {
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
            lastResult = func(...args);
        }
        return lastResult;
    };
};

// --- Type Definitions ---
interface AnalyticsSessionData {
    totalTimeSpentSeconds: number;
    scrollDepthReached: number;
    linkClicks: { blockId: string; url: string; count: number }[];
    shareClicks: { platform: string; count: number }[];
}
interface useProfileAnalyticsProps {
    user: IUserData; // Ensure this is part of the props if you intend to use it
    pageId?: string | null;
}

// --- Main Hook ---
export function useProfileAnalytics({
    user, // Include user in destructuring if passed down
    pageId,
}: useProfileAnalyticsProps) {
    // State to hold the accumulated session data
    const [sessionAnalytics, setSessionAnalytics] =
        useState<AnalyticsSessionData>({
            totalTimeSpentSeconds: 0,
            scrollDepthReached: 0,
            linkClicks: [],
            shareClicks: [],
        });
    // Ref to track if the initial 'view' has been successfully logged
    const initialViewLoggedRef = useRef(false);

    // Ref to hold the latest sessionAnalytics state for use in cleanup/unloading
    const latestSessionAnalyticsRef = useRef(sessionAnalytics);
    useEffect(() => {
        latestSessionAnalyticsRef.current = sessionAnalytics;
    }, [sessionAnalytics]); // This ensures the ref is always up-to-date

    // Ref for the start time of the session
    const startTimeRef = useRef(Date.now());

    // --- Analytics Logging Core Function ---
    // This function's sole purpose is to update the local sessionAnalytics state.
    // It should NOT contain debouncing/throttling/deduplication logic internally.
    const logEventLocally = useCallback(
        (
            eventType:
                | "view"
                | "heartbeat"
                | "scroll"
                | "linkClick"
                | "shareClick"
                | "textSelect"
                | "unload",
            data: Record<string, any> = {}
        ) => {
            setSessionAnalytics((prev) => {
                const newState = { ...prev }; // Always create a new object
                switch (eventType) {
                    case "unload":
                        newState.totalTimeSpentSeconds = Math.max(
                            prev.totalTimeSpentSeconds, // Use prev state to ensure continuous accumulation
                            data.time_spent_seconds !== undefined
                                ? Math.floor(data.time_spent_seconds)
                                : prev.totalTimeSpentSeconds
                        );
                        break;
                    case "scroll":
                        if (newState.scrollDepthReached <= data.percentage) {
                            newState.scrollDepthReached = data.percentage;
                        }
                        break;
                    case "shareClick":
                        const platform = data.platform;
                        const existingShareClickIndex =
                            newState.shareClicks.findIndex(
                                (item) => item.platform === platform
                            );

                        if (existingShareClickIndex !== -1) {
                            newState.shareClicks = newState.shareClicks.map(
                                (item, idx) =>
                                    idx === existingShareClickIndex
                                        ? { ...item, count: item.count + 1 }
                                        : item
                            );
                        } else {
                            newState.shareClicks = [
                                ...newState.shareClicks,
                                { platform: platform, count: 1 },
                            ];
                        }
                        break;
                }
                return newState;
            });
        },
        [] // Empty dependency array as `setSessionAnalytics` is stable
    );

    // --- 1. Page View & Time on Page ---
    useEffect(() => {
        // Log initial view only once per component instance (persists across StrictMode re-renders)
        if (!initialViewLoggedRef.current) {
            logEventLocally("view");
            initialViewLoggedRef.current = true;
            console.log("ANALYTICS (INITIAL LOAD) - View logged.");
        }

        const handleBeforeUnload = () => {
            const endTime = Date.now();
            const timeSpent = (endTime - startTimeRef.current) / 1000;

            // Use the value from the mutable ref to get the absolute latest state
            const finalAnalyticsData = {
                ...latestSessionAnalyticsRef.current, // This should have the latest state
                totalTimeSpentSeconds: Math.floor(timeSpent), // Override with final exact time
            };
            // Also ensure views count is accurate.
            // If the component mounted only once, views will be 1. If StrictMode caused a double mount, it might be 2.
            // The `initialViewLoggedRef` ensures `logEventLocally("view")` only increments `views` once,
            // so `latestSessionAnalyticsRef.current.views` should already be accurate.
            navigator.sendBeacon(
                "/api/user-stats", // Your endpoint to save final session data
                JSON.stringify({
                    userId: user?.id, // Include userId from props
                    pageId,
                    ...finalAnalyticsData,
                })
            );
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            // clearInterval(heartbeatInterval);
            window.removeEventListener("beforeunload", handleBeforeUnload);
            // On component unmount (e.g., SPA navigation), also send data for non-beacon scenario
            const endTime = Date.now();
            const timeSpent = (endTime - startTimeRef.current) / 1000;
            logEventLocally("unload", { time_spent_seconds: timeSpent });
            console.log("ANALYTICS (Component Unmount) - Unload logged.");
        };
    }, [user?.id, logEventLocally]); // Add user.id to dependencies

    // --- 2. Scroll Depth Tracking ---
    useEffect(() => {
        const handleScroll = () => {
            const targetElement = document.getElementById("root-profile");
            if (!targetElement) {
                // console.warn("Element with ID 'user-root' not found for scroll tracking.");
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = targetElement;
            const totalScrollable = scrollHeight - clientHeight;

            if (totalScrollable > 0) {
                const scrollPercentage = Math.min(
                    100,
                    Math.round((scrollTop / totalScrollable) * 100)
                );

                if (scrollPercentage >= 25) {
                    logEventLocally("scroll", { percentage: 25 });
                }
                if (scrollPercentage >= 50) {
                    logEventLocally("scroll", { percentage: 50 });
                }
                if (scrollPercentage >= 75) {
                    logEventLocally("scroll", { percentage: 75 });
                }
                if (scrollPercentage === 100) {
                    logEventLocally("scroll", { percentage: 100 });
                }
            }
        };

        const currentScrollElement = document.getElementById("root-profile");
        if (currentScrollElement) {
            // Apply throttling to scroll events to prevent performance issues
            const throttledHandleScroll = throttle(handleScroll, 200); // Throttle by 200ms
            currentScrollElement.addEventListener(
                "scroll",
                throttledHandleScroll
            );
            return () => {
                currentScrollElement.removeEventListener(
                    "scroll",
                    throttledHandleScroll
                );
            };
        }
    }, [
        logEventLocally, // dependency for memoization, not for re-running logic within
    ]);

    useEffect(() => {
        const abortController = new AbortController();
        window.addEventListener(
            "user-profile-share-click-track",
            (event: any) => {
                if (event.detail) {
                    logEventLocally("shareClick", {
                        platform: event.detail.platform,
                    });
                }
            },
            { signal: abortController.signal }
        );
        return () => {
            abortController.abort();
        };
    });
    // Return the accumulated session data and functions
    return {
        sessionAnalytics, // Return the accumulated data for the component to display/use
    };
}
